import { Redirect, router } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import {
  AthleteProfileForm,
  satisfies,
  type AthleteProfileValues,
} from '@/components/gifting/AthleteProfileForm';
import { colors, spacing } from '@/constants/theme';
import { campaignCopy } from '@/lib/campaignCopy';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { accountMergeService } from '@/services/accountMerge';
import { API_ERROR_CODE, isApiError } from '@/services/errors';
import { giftingService } from '@/services/gifting';
import { authStore } from '@/stores/auth.store';

/**
 * The one authenticated profile screen. Reached only after a PIN, so we know
 * exactly who this is and prefill from their OWN record — never from a lookup
 * keyed on something they typed, which would be a profile-harvesting oracle.
 *
 * It decides nothing about eligibility. The server's `gifting_next_step` says
 * which screen this user belongs on and we render only if that answer is us,
 * so the campaign's rules can change without an app release.
 */
const CompleteScreen = observer(() => {
  const user = authStore.user;

  const [values, setValues] = useState<AthleteProfileValues>({
    firstName: user?.first_name ?? '',
    lastName: user?.last_name ?? '',
    email: user?.email ?? '',
    school: user?.school?.name ?? null,
    sport: user?.sport ?? null,
    instagram: user?.instagram_handle ?? '',
    tiktok: user?.tiktok_handle ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailConflict, setEmailConflict] = useState(false);
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [linkRequesting, setLinkRequesting] = useState(false);

  function set<K extends keyof AthleteProfileValues>(key: K, value: AthleteProfileValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  if (!authStore.hydrated) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator color={colors.deepGreen} />
      </View>
    );
  }
  if (!authStore.isAuthenticated) {
    return <Redirect href={GIFTING_ROUTES.start} />;
  }
  // The server owns whether this screen renders: complete → grid, claimed →
  // gift, ineligible (null) → landing. Without the null case a signed-in
  // user could fill six fields and only then get 403'd.
  const step = giftingRouteFor(user);
  if (step !== GIFTING_ROUTES.complete) {
    return <Redirect href={step ?? GIFTING_ROUTES.landing} />;
  }

  // The server names what it still needs. No required-field list lives here,
  // so adding one server-side doesn't need an app release.
  const missing = user?.campaign_profile_missing ?? [];
  const ready = satisfies(missing, values);

  async function submit() {
    if (!ready || submitting) return;
    // Server says school/sport aren't missing but local objects are absent
    // (payload edge) — surface it instead of a silently dead button.
    if (!values.school || !values.sport) {
      setSubmitError('Pick your school and sport to continue.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setEmailConflict(false);
    setPhoneRequired(false);
    try {
      await giftingService.completeAthleteProfile({
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email: values.email.trim(),
        school: values.school.trim(),
        sport: values.sport,
        instagram_handle: values.instagram.trim(),
        tiktok_handle: values.tiktok.trim() || undefined,
      });
      await authStore.refreshMe();
      // Where to go next is the server's answer on the refreshed payload,
      // not an assumption that completing always means the grid. A null step
      // means the campaign closed under them mid-form — send them to the
      // landing, which explains it, instead of a grid that would 403.
      router.replace(giftingRouteFor(authStore.user) ?? GIFTING_ROUTES.landing);
    } catch (err) {
      if (isApiError(err) && err.code === API_ERROR_CODE.EmailTaken) {
        setEmailConflict(true);
      } else if (isApiError(err) && err.code === 'phone_required') {
        // Their account has no phone, and this screen can't add one — only a
        // PIN can. This is the ~14% of imported athletes whose AC contact
        // carried an email but no number, so it is a normal outcome, not an
        // edge case: give them the door rather than a sentence describing it.
        setPhoneRequired(true);
      } else if (isApiError(err)) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Could not save your profile. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // The phone_required answer. Signing out first is load-bearing, not
  // housekeeping: start.tsx redirects any authenticated user to
  // giftingRouteFor(user), which for a phone-less athlete resolves back to
  // THIS screen — so navigating there while signed in is an instant round
  // trip. Signing out lets the phone door render its form, and the number
  // they enter mints a session whose email collision hands them to the
  // link-account merge, which restores this account with the phone attached.
  async function goToPhoneDoor() {
    if (leaving) return;
    setLeaving(true);
    try {
      await authStore.logout();
      router.replace(GIFTING_ROUTES.start);
    } catch {
      // Logout is local-first; if the network call fails the session is still
      // cleared, so the door is still the right destination.
      router.replace(GIFTING_ROUTES.start);
    }
  }

  // The email_taken answer: send a code to the claimed email, then hand off
  // to the link-account screen where typing it merges this phone into that
  // account. The request runs HERE so the screen opens knowing the send
  // already happened (and with the masked address to display).
  async function startLink() {
    const email = values.email.trim();
    if (!email || linkRequesting) return;
    setLinkRequesting(true);
    setSubmitError(null);
    try {
      const { contact_masked } = await accountMergeService.request(email);
      router.push({
        pathname: GIFTING_ROUTES.linkAccount,
        params: { email, masked: contact_masked },
      });
    } catch (err) {
      setSubmitError(
        isApiError(err) ? err.message : 'Could not send the code. Please try again.',
      );
    } finally {
      setLinkRequesting(false);
    }
  }

  return (
    // keyboardAvoiding is not optional here: this form runs past the fold
    // (name, school, sport, Instagram) and without it the keyboard covers the
    // lower fields and the submit button — you type blind. confirm.tsx already
    // opts in; this screen and start.tsx were missed. Reported from a real
    // device 2026-08-09.
    <Screen keyboardAvoiding width="column">
      {/* Explicit target, same as choose: the campaign index REDIRECTS here
          for incomplete profiles, so a history pop would bounce straight
          back — a visible no-op. Home is the one place that's always safe. */}
      <BackChevron onPress={() => router.replace('/(tabs)')} />
      <View style={styles.body}>
        <Overline>{campaignCopy(authStore.user?.gifting_copy).name}</Overline>
        <Heading size="4xl" style={styles.headline}>
          Almost yours.
        </Heading>
        <Text color="textMuted" style={styles.copy}>
          A few details so the brand knows who they&apos;re sending it to.
        </Text>

        <View style={styles.form}>
          <AthleteProfileForm
            values={values}
            onChange={set}
            needsEmail={missing.includes('email')}
          />

          {emailConflict && (
            <>
              <Text variant="caption" color="danger">
                That email already has a MarketPryce account. Verify it&apos;s you and we&apos;ll
                link your phone number to it — your gift comes with you.
              </Text>
              <Button
                label={linkRequesting ? 'Sending code…' : 'Email me a code'}
                variant="outline"
                onPress={() => void startLink()}
                disabled={linkRequesting}
              />
              <Button
                label="Sign in with that email instead"
                variant="ghost"
                onPress={() =>
                  router.push({
                    pathname: '/(auth)/login',
                    params: { prefill_contact: values.email.trim(), redirect: GIFTING_ROUTES.complete },
                  })
                }
              />
            </>
          )}

          {phoneRequired && (
            <>
              <Text variant="caption" color="danger">
                We need a phone number to send your gift. Continue with your number and
                we&apos;ll link it to this account — your details come with you.
              </Text>
              <Button
                label={leaving ? 'One moment…' : 'Continue with my phone number'}
                onPress={() => void goToPhoneDoor()}
                disabled={leaving}
              />
            </>
          )}

          {submitError && (
            <Text variant="caption" color="danger">
              {submitError}
            </Text>
          )}

          {/* Hidden once we know a phone is required: this button can only ever
              fail from here, and leaving it as the primary action invites the
              user to press it forever. */}
          {!phoneRequired && (
            <Button
              label={submitting ? 'Saving…' : 'See the gifts'}
              onPress={() => void submit()}
              disabled={!ready || submitting}
            />
          )}
          <Text variant="caption" color="textMuted" align="center">
            Your shipping info is shared only with the brand sending your gift.
          </Text>
        </View>
      </View>
    </Screen>
  );
});

export default CompleteScreen;

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  body: {
    paddingTop: spacing.sm,
  },
  headline: {
    marginTop: spacing.md,
  },
  copy: {
    marginTop: spacing.base,
  },
  form: {
    marginTop: spacing.lg,
    gap: spacing.base,
  },
});
